import * as _ from 'lodash';

import { EMPTY_GEOBLOCK } from '@/components/Geoblock/GeoblockConstants';
import { AssetSource } from '@/enums';
import type {
  AccessSettings,
  CommonBusinessMetadata,
  CommonTagKeys,
  DisplaySettings,
  LinkedAssetInfo,
  PublicationStatus,
} from '@/models/channel';
import { AccessLevelType, PublicationStatusType } from '@/models/channel';
import type { Geoblock } from '@/models/Geoblock';
import { enhanceFetchedAssets } from '@/pages/assets/asset-listing/enhanceFetchedAsset';

import type { Item } from '../../../models';
import type { ClipGroup, CustomMetaFieldInfo } from '../../../models/item';
import type { ContentSharingBehaviors, OU } from '../../../models/Ou';
import { Permissions } from '../../../models/Permissions';
import { ValidationErrors } from '../../../models/ValidationErrors';
import { FuelEntityType, FuelPortalService } from '../../../services';
import clientUtils from '../../../utils/clientUtils';
import { dateUtils } from '../../../utils/dateUtils';
import itemUtils from '../../../utils/itemUtils';
import generateAssetFormHashCode from './generateAssetFormHashCode';
import { SchemaType, SchemaTypeOptions } from './SchemaTypeOptions';

export type Mode = 'new' | 'existing';

const longFormTagFields = [
  'rating-mpaa',
  'rating-bbfc',
  'episode-xmltv',
  'episode-onscreen',
  'actor',
  'producer',
  'director',
  'genre',
  'season',
  'episode',
  'series',
];

export const defaultCustomFields: CustomMetaFieldInfo[] = [
  // {
  //   key: 'isLongFormContentEnabled',
  //   friendlyName: 'Movie / Series Episode',
  //   value: 'true',
  // },
  {
    key: 'subtitle',
    friendlyName: 'Subtitle',
    value: '',
  },
  // moved all the below over to tags field
  // {
  //   key: 'mpaaRating',
  //   friendlyName: 'MPAA Rating',
  //   value: '',
  // },
  // {
  //   key: 'bbfcRating',
  //   friendlyName: 'BBFC Rating',
  //   value: '',
  // },
  // {
  //   key: 'episodeNumberXMLTV',
  //   friendlyName: 'Episode Number - xmltv',
  //   value: '',
  // },
  // {
  //   key: 'episodeNumberOnScreen',
  //   friendlyName: 'Episode Number - On Screen',
  //   value: '',
  // },
  // {
  //   key: 'cast',
  //   friendlyName: 'Cast',
  //   value: '',
  // },
  // {
  //   key: 'producers',
  //   friendlyName: 'Producers',
  //   value: '',
  // },
  // {
  //   key: 'directors',
  //   friendlyName: 'Directors',
  //   value: '',
  // },
  // {
  //   key: 'genre',
  //   friendlyName: 'Genre',
  //   value: '',
  // },
  // ... more to come
  // {
  //   key: 'subtitle',
  //   friendlyName: 'Subtitle',
  //   value: '',
  // },
];

export class AssetForm {
  mode: Mode = 'new';
  permissions: Permissions = new Permissions([]);
  assetDisplayNameEnabled: boolean = false; // this comes from a feature flag...
  errors: ValidationErrors = new ValidationErrors([]);
  // only relevant for new asset creation (not existing)...
  ingestFile: File | null = null;
  ingestFiles: File[] = [];
  isBatchUploadEnable: boolean = false;
  originalFileName: string | null = null;
  id: string | null = null;
  title: string = '';
  source: string = '';
  publicationDate: string | null = null;
  automatedTitle: string = '';
  synopsis: string = '';
  categories: Array<string> = [];
  tags: Array<string> = [];
  embargoDate: string | null = null;
  expireDate: string | null = null;
  contentSharingBehavior: ContentSharingBehaviors = 'DoNotShare';
  applyAutomatedTransitions: boolean = true;
  includeAsTransition: boolean = false;
  assetSrc: string = '';
  didPlayerError: boolean = false;
  originalAssetData: Item | null = null;
  initialHashCode: string = '';
  // Long form content custom fields
  isLongFormContentEnabled: boolean = false; // found in businessMetadata.customFields
  customFields: CustomMetaFieldInfo[] = [];
  // needed for clipGroup asset
  groupName: string = '';
  clipIds: string[] = [];
  clipGroupContentClips: Item[] = [];
  autoGroupingId: string | null = null;

  schemaType: SchemaType = SchemaType.Video;
  doNotDelete: boolean = false;
  accessSettings: AccessSettings | null = {
    accessLevel: AccessLevelType.Free,
    effectiveAccessLevel: AccessLevelType.Free,
    geographic: EMPTY_GEOBLOCK,
    demographic: {
      ageGate: null,
    },
    temporal: {
      accessSchedules: [],
    },
  };
  linkedAssets: LinkedAssetInfo[] = [];
  displaySettings: DisplaySettings = {
    background: null,
    backgroundColor: '#ffffff',
    logo: null,
    thumbnail: null,
    poster: null,
    favIcon: null,
    badge: null,
  };
  tmp: {
    itemLookup: Record<string, string>;
    sectionsOpen: boolean[];
  } = {
      itemLookup: {},
      sectionsOpen: [false, false, false],
    };
  common: CommonBusinessMetadata = {
    description: '',
    highlightMessage: '',
    cast: [],
    producer: [],
    director: [],
    mpaaRating: '',
    bbfcRating: '',
    publicationDate: null,
    releaseYear: '',
    tags: [],
    categories: [], // a list of category names only (possibility of corruption if ou category names change)
    genre: [],
    series: [],
    season: [],
    episodeXmltv: '',
    episodeOnscreen: '',
    usParentalRatings: '',
    europeanFskRating: '',
    teamName: [],
    leagueName: [],
    originalAirDate: '',
    networkValue: '',
    tmsidEpisode: '',
    tmsidSeries: '',
    programType: '',
    episode: '',
  };
  publicationStatus: PublicationStatus = {
    status: PublicationStatusType.Unknown,
  };
  markers = [];
  markersForPlayer = [];
  isTransition: boolean = false;
  subTitle: string = '';
  hlsFileUrl: string = '';
  externalHlsFileUrl: string = '';
  clipGroup: ClipGroup | null = null;
  geoblock: Geoblock | null = null;
  isSourceFileDownloadUrl: boolean = false;

  constructor() { }

  static create(permissions: Permissions, ouData: OU, source: string = ''): AssetForm {
    // console.log('AssetForm.create', { permissions, ouData, source });
    const form = new AssetForm();
    form.mode = 'new';
    form.permissions = permissions;
    form.assetDisplayNameEnabled = false;
    form.errors = new ValidationErrors([]);
    form.ingestFile = null;
    form.originalFileName = null;
    form.originalAssetData = null;
    form.title = '';
    form.source = source;
    form.automatedTitle = '';
    form.synopsis = '';
    form.publicationDate = dateUtils.getTimestamp(ouData.timezone);
    form.categories = [];
    form.tags = [];
    form.embargoDate = null;
    form.expireDate = null;
    form.contentSharingBehavior =
      ouData.metaData?.enableCrossOuSharing === true
        ? (ouData.metaData?.contentSharing?.behavior ?? 'DoNotShare')
        : 'DoNotShare';
    form.applyAutomatedTransitions = true;
    form.includeAsTransition = false;
    form.assetSrc = '';
    form.didPlayerError = false;
    form.initialHashCode = generateAssetFormHashCode(form);
    // Long form content custom fields
    form.isLongFormContentEnabled = false;
    form.customFields = [];
    form.accessSettings.accessLevel = ouData?.metaData?.defaultAccessLevel || AccessLevelType.Free;
    form.accessSettings.effectiveAccessLevel = ouData?.metaData?.defaultAccessLevel || AccessLevelType.Free;
    form.clipGroup = AssetForm.getClipGroup(form, ouData, null);
    return form;
  }

  static getMarkers = (asset: Item, formatVideoTime: boolean = true) => {
    const newMarkers = [];
    const item = asset;
    let index: number = -1;
    const myFrameRate = itemUtils.getFrameRate(item);
    if (item && item.data && item.data.timeStampMarkers && item.data.timeStampMarkers.length > 0) {
      index = _.findIndex(item.data.timeStampMarkers, (x) => x.markerType === 'AdInsertionPoints');
      if (index > -1) {
        const timeStampMarkers = item.data.timeStampMarkers[index].timeStamps;
        for (let i = 0; i < timeStampMarkers.length; i++) {
          const marker = {
            time: timeStampMarkers[i],
            text: `Chapter${[i + 1]}`,
          };
          if (formatVideoTime) {
            const currentTimeFormatted = clientUtils.formatVideoTime(marker.time, myFrameRate);
            newMarkers.push(currentTimeFormatted);
          } else {
            newMarkers.push(marker);
          }
        }
      }
    }
    return newMarkers;
  };

  static async createFromData(
    asset: Item,
    permissions: Permissions,
    ouData?: OU,
    createFormForSubclip: boolean = false,
    featureFlags: any = null
  ): Promise<AssetForm> {
    // console.log('AssetForm.createFromData', { asset, permissions, ouData, createFormForSubclip });

    const form = new AssetForm();
    form.mode = 'existing';
    form.id = asset.id;
    form.permissions = permissions;
    form.assetDisplayNameEnabled = false;
    form.errors = new ValidationErrors([]);
    form.originalAssetData = asset;
    form.originalFileName = asset.data.originalFileName;
    form.title = asset.data.businessMetadata.title || '';
    form.source = asset.source;
    form.automatedTitle = asset.data.businessMetadata.automatedTitle || '';
    form.synopsis = asset.data.businessMetadata.synopsis || '';
    form.publicationDate = asset.data.businessMetadata.publicationDate || '';

    let schema = SchemaTypeOptions.find((s) => {
      return s.id == asset.schemaType;
    });
    // aalt fallback to schemaName
    if (schema === undefined && asset.schemaName) {
      schema = SchemaTypeOptions.find((s) => {
        return s.id == asset.schemaName;
      });
    }
    if (schema === undefined) throw `Unexpected state, schema id: '${asset.schemaType}' was not available in schemas?!?`;

    form.schemaType = schema.id;

    // type signature says possibility of Array<string> but in real life it comes to us as an Array<Category>...
    form.categories = asset.categories;
    form.tags = asset.tags;

    form.embargoDate = asset.data.accessSettings.effectiveEmbargoDate;
    form.expireDate = asset.data.accessSettings.effectiveExpireDate;
    form.applyAutomatedTransitions = asset.applyAutomatedTransitions === null ? false : asset.applyAutomatedTransitions;

    const { assetSrc, isSourceFileDownloadUrl } = await this.getAssetSrc(asset, createFormForSubclip);
    form.assetSrc = assetSrc;
    form.isSourceFileDownloadUrl = isSourceFileDownloadUrl;

    form.didPlayerError = false;

    // Long form content custom fields
    form.isLongFormContentEnabled = false;
    form.customFields = [];
    if (
      asset.data.businessMetadata &&
      asset.data.businessMetadata.customFields &&
      asset.data.businessMetadata.customFields.length > 0
    ) {
      for (let i = 0; i < asset.data.businessMetadata.customFields.length; i++) {
        const element = asset.data.businessMetadata.customFields[i];
        if (element.key === 'subtitle' && element.value) {
          form.isLongFormContentEnabled = true;
        }
      }
      form.customFields = asset.data.businessMetadata.customFields;
    }

    if (asset && asset.data) {
      form.doNotDelete = asset.data.doNotDelete;
    }
    // console.log('asset.tags', asset.tags);
    for (let j = 0; j < asset.tags.length; j++) {
      const tag = asset.tags[j];
      // console.log('tag', tag);
      if (itemUtils.isMetaTag(tag)) {
        const { key, value } = itemUtils.getMetaTagKeyValue(tag);
        if (_.includes(longFormTagFields, key)) {
          form.isLongFormContentEnabled = true;
          break;
        }
      }
    }
    form.markers = AssetForm.getMarkers(asset);
    form.markersForPlayer = AssetForm.getMarkers(asset, false);

    if (asset?.tags?.length > 0) {
      // populate fields from tags
      const myState: CommonTagKeys = itemUtils.extractCommonTags(asset.tags);

      form.common.cast = myState.cast;
      form.common.producer = myState.producer;
      form.common.director = myState.director;
      form.common.mpaaRating = itemUtils.getFirstOrDefault(myState.ratingMpaa, '');
      form.common.bbfcRating = itemUtils.getFirstOrDefault(myState.ratingBbfc, '');
      form.common.releaseYear = myState.releaseYear;
      form.common.teamName = myState.teamName;
      form.common.tags = myState.tags;
      form.common.genre = myState.genre;
      form.common.series = myState.series;
      form.common.season = myState.season;
      form.common.episode = myState.episode;
      form.common.episodeOnscreen = myState.episodeOnscreen;
      form.common.episodeXmltv = myState.episodeXmltv;
      form.common.europeanFskRating = myState.europeanFskRating;
      form.common.usParentalRatings = myState.usParentalRatings;
      form.common.leagueName = myState.leagueName;
      form.common.originalAirDate = myState.originalAirDate;
      form.common.networkValue = myState.networkValue;
      form.common.tmsidEpisode = myState.tmsidEpisode;
      form.common.tmsidSeries = myState.tmsidSeries;
      form.common.programType = myState.programType;
    }
    form.common.categories = asset.categories.map((cat) => cat.id);
    // form.common.publicationDate = myState.publicationDate;
    form.common.highlightMessage = asset.data.businessMetadata.highlightMessage || '';
    if (asset?.data?.accessSettings) {
      form.accessSettings = asset.data.accessSettings;
    }

    if (asset?.data?.linkedAssets != null) {
      form.linkedAssets = asset.data.linkedAssets;
    }
    form.displaySettings = clientUtils.populateDisplaySettings(asset?.data?.linkedAssets, asset?.data?.displaySettings);

    if (asset?.data.publicationStatus) {
      form.publicationStatus = asset.data.publicationStatus;
    }
    if (asset?.data.publicationStatus === null || asset?.data?.accessSettings === null) {
      form.publicationStatus = {
        status: PublicationStatusType.Published,
      };
    }
    if (asset?.data?.businessMetadata?.subTitle) {
      form.subTitle = asset.data.businessMetadata.subTitle;
    }
    if (asset.hlsFileUrl) {
      form.hlsFileUrl = asset.hlsFileUrl;
    }
    if (asset.source === AssetSource.ExternalClip) {
      form.externalHlsFileUrl = asset.data.clipHLSData.externalHLSUrl;
    }
    if (asset.source === AssetSource.ClipGroup) {
      const { clipGroup, clips } = await AssetForm.loadClipGroupData(asset);
      form.clipGroup = clipGroup;
      form.clipIds = clips.map((c) => c.id);
      form.groupName = clipGroup.groupName;
      form.autoGroupingId = clipGroup.autoGroupingId;

      if (featureFlags) {
        form.clipGroupContentClips = enhanceFetchedAssets(clips, permissions, featureFlags, true, [], [], ouData);
      }
    }

    const crossOuSharingEnabled = ouData?.metaData?.enableCrossOuSharing === true;
    if (asset.data.contentSharing != null) {
      form.contentSharingBehavior = (asset.data.contentSharing.behavior ??
        (crossOuSharingEnabled ? ouData?.metaData?.contentSharing?.behavior : null) ??
        'DoNotShare') as ContentSharingBehaviors;
    } else if (crossOuSharingEnabled) {
      form.contentSharingBehavior = ouData?.metaData?.contentSharing?.behavior ?? 'DoNotShare';
    } else {
      form.contentSharingBehavior = 'DoNotShare';
    }

    form.initialHashCode = generateAssetFormHashCode(form);

    return form;
  }

  static createFromFile(file: File, ouData: OU) {
    const form = new AssetForm();
    form.mode = 'new';
    form.ingestFile = file;
    form.originalFileName = file.name;
    form.schemaType = clientUtils.getSchemaTypeByExtension(file);

    form.title = file.name.split('.').slice(0, -1).join('.') || file.name;
    form.source = 'Uploaded File';
    form.automatedTitle = form.title;

    form.publicationDate = dateUtils.getTimestamp(ouData.timezone);

    form.categories = [];
    form.tags = [];

    form.embargoDate = null;
    form.expireDate = null;
    form.contentSharingBehavior =
      ouData.metaData?.enableCrossOuSharing === true
        ? (ouData.metaData?.contentSharing?.behavior ?? 'DoNotShare')
        : 'DoNotShare';
    form.applyAutomatedTransitions = true;

    form.assetSrc = URL.createObjectURL(file);
    form.didPlayerError = false;
    form.initialHashCode = generateAssetFormHashCode(form);

    form.isLongFormContentEnabled = false;
    form.customFields = [...defaultCustomFields];

    form.accessSettings = {
      accessLevel: ouData?.metaData?.defaultAccessLevel || AccessLevelType.Free,
      effectiveAccessLevel: ouData?.metaData?.defaultAccessLevel || AccessLevelType.Free,
    };

    return { ...form };
  }

  static async getAssetSrc(asset: Item, createFormForSubclip: boolean) {
    // console.log('getAssetSrc', { asset, createFormForSubclip });
    if (asset.source === AssetSource.ExternalClip) {
      return { assetSrc: asset.hlsFileUrl, isSourceFileDownloadUrl: false };
    }
    let isSourceFileDownloadUrl = false;
    if (!asset.hlsFileUrl && !createFormForSubclip) {
      isSourceFileDownloadUrl = true;
      const isVideoProxy = asset.schemaType === 'Graphics';
      const url = await FuelPortalService.getDownloadLink(asset.id, asset.data.originalFileName, isVideoProxy);
      asset.data.videoProxy = {
        previewUri: url,
      };
    }
    // @ts-ignore - lots of Item types running around, this seems to work though even if the types aren't lining up exactly...
    const assetSrc = itemUtils.getVideoProxyUrl(asset);
    return { assetSrc, isSourceFileDownloadUrl };
  }

  static async loadClipGroupData(asset: Item) {
    try {
      const clipGroup = await FuelPortalService.getEntityById(FuelEntityType.ClipGroup, asset.id);

      if (clipGroup && clipGroup.clipIds.length > 0) {
        const clips: Item[] = await FuelPortalService.batchGet(FuelEntityType.Clip, clipGroup.clipIds);

        return { clipGroup, clips };
      }

      return { clipGroup, clips: [] };
    } catch (ex) {
      console.error(ex);
      return { clipGroup: null, clips: [] };
    }
  }

  static getClipGroup(formData: AssetForm, ouData: OU, autoGroupingId: string) {
    const clipGroup: ClipGroup = {
      id: formData.mode === 'new' ? null : formData.id,
      entityOuId: ouData.ouId,
      clipIds: formData.clipIds,
      groupName: formData.title,
      autoGroupingId: formData.mode === 'new' ? null : autoGroupingId,
      entityType: FuelEntityType.ClipGroup,
    };
    return clipGroup;
  }
}
